import {
  drishtiApi,
  DrishtiApiError,
  type DrishtiQuery,
  type DrishtiRequestOptions,
  type DrishtiResponse,
} from './drishti-client.js';
import { isMcpConfigured, callMcpToolByName } from './drishti-mcp-client.js';
import { capabilityMap } from './mcp-catalog.js';
import { logger } from '../../utils/logger.js';

/**
 * Automatic REST -> MCP fallback for Drishti GET calls.
 *
 * REST stays primary. When a REST call fails with a rate-limit / quota error
 * (429, 402, 403) AND the MCP layer is configured AND an equivalent MCP tool is
 * mapped for the endpoint, we transparently retry via MCP and return the data —
 * no user intervention. A 401 (invalid/expired key) is NOT retried because MCP
 * authenticates with the same key. Any other failure, or a rate-limit error
 * with no MCP equivalent, throws an actionable error.
 */

const FALLBACK_STATUSES = new Set([429, 402, 403]);

export function shouldFallback(status: number): boolean {
  return FALLBACK_STATUSES.has(status);
}

/**
 * GET with automatic MCP fallback. Drop-in for `drishtiApi.get`, returning the
 * same `{ data, url }` shape. `capabilityKey` defaults to the endpoint.
 */
export async function drishtiGetWithFallback<T>(
  endpoint: string,
  params: DrishtiQuery = {},
  options: DrishtiRequestOptions = {},
  capabilityKey?: string,
): Promise<DrishtiResponse<T>> {
  try {
    return await drishtiApi.get<T>(endpoint, params, options);
  } catch (error) {
    if (!(error instanceof DrishtiApiError)) throw error;

    // Invalid/expired key — MCP uses the same key, so a retry cannot help.
    if (error.status === 401) throw error;

    const mapping = capabilityMap[capabilityKey ?? endpoint];

    if (shouldFallback(error.status) && isMcpConfigured() && mapping) {
      try {
        const raw = await callMcpToolByName(
          mapping.mcpTool,
          mapping.transformArgs(params as Record<string, unknown>),
        );
        const envelope = JSON.parse(raw) as { data?: unknown };
        logger.info(
          `[Drishti] REST ${endpoint} failed (${error.status}); served via MCP ${mapping.mcpTool}`,
        );
        return { data: envelope.data as T, url: `mcp://drishti/${mapping.mcpTool}` };
      } catch (mcpError) {
        const message = mcpError instanceof Error ? mcpError.message : String(mcpError);
        logger.warn(`[Drishti] MCP fallback for ${endpoint} failed: ${message}`);
        // Fall through to the actionable rate-limit error below.
      }
    }

    if (shouldFallback(error.status)) {
      const reason = isMcpConfigured()
        ? 'no equivalent MCP tool is available for this request'
        : 'the MCP fallback layer is not configured (set DRISHTI_MCP_URL)';
      throw new Error(
        `Drishti rate/quota limit hit on ${endpoint} (HTTP ${error.status}) and ${reason}. ` +
          `Retry shortly, or call drishti_account_status to check credits and limits.`,
      );
    }

    throw error;
  }
}
